import React, {useEffect, useRef, useState} from "react";
import Button from "@rescui/button";
import {Input} from "@rescui/input";
import {GridDialog} from "../../grid/ui/GridDialog";
import {operateGridProject, projectSnapshotId, ProjectRequestError, type ProjectOperationAttempt, type ProjectSummary} from "../../grid/projects";

type Action = "rename" | "duplicate" | "trash" | "restore" | "import";
export type ProjectAction = {action: Action; project?: ProjectSummary};
const labels: Record<Action, string> = {rename: "Rename project", duplicate: "Duplicate project", trash: "Move to Trash", restore: "Restore project", import: "Import snapshot"};
export function ProjectActionDialog({selection, onClose, onComplete}: {selection: ProjectAction; onClose: () => void; onComplete: () => Promise<void>}) {
  const {action, project} = selection;
  const [value, setValue] = useState(action === "duplicate" ? `${project.title.slice(0, 113)} (copy)` : action === "rename" ? project.title : "");
  const [pending, setPending] = useState(false), [error, setError] = useState(""), [uncertain, setUncertain] = useState(false), [conflict, setConflict] = useState(false);
  const attempt = useRef<ProjectOperationAttempt | null>(null), busy = useRef(false), mounted = useRef(true);
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  const execute = async () => {
    if (busy.current || conflict) return;
    setError("");
    try {
      if (!attempt.current) {
        const key = crypto.randomUUID();
        attempt.current = action === "import" ? {action, key, snapshotId: projectSnapshotId(value, window.location.origin)} :
          action === "rename" || action === "duplicate" ? {action, key, projectId: project.projectId, revision: project.revision, title: value} :
          {action, key, projectId: project.projectId, revision: project.revision};
      }
      busy.current = true; setPending(true);
      await operateGridProject(attempt.current);
      if (mounted.current) await onComplete();
    } catch (reason) {
      if (!mounted.current) return;
      const definitive = reason instanceof ProjectRequestError && reason.status < 500;
      const unknown = busy.current && !definitive;
      if (!unknown) attempt.current = null;
      setUncertain(unknown); setConflict(reason instanceof ProjectRequestError && reason.status === 409);
      setError(reason instanceof Error ? reason.message : "Project operation failed");
    } finally {busy.current = false; if (mounted.current) setPending(false);}
  };
  const close = () => {if (!busy.current) onClose();};
  return <GridDialog title={labels[action]} onClose={close}>
    {project && <p>{project.title || "Untitled grid"}</p>}
    {action === "trash" && <p>The project will move to Trash. You can restore it later. Uploaded files and shared snapshot links are preserved.</p>}
    {action === "restore" && <p>Return this saved Grid to My projects.</p>}
    {action === "duplicate" && <p>Creates a separate project from the saved Grid. Unsaved edits in other tabs are not copied.</p>}
    {action === "import" && <p>Paste a snapshot link from this Coverbaker, or its snapshot ID. You need access to its channels and images.</p>}
    {["rename", "duplicate", "import"].includes(action) && <>
      <label htmlFor="project-action-value">{action === "import" ? "Snapshot link or ID" : "Project name"}</label>
      <Input id="project-action-value" autoFocus value={value} maxLength={action === "import" ? 2048 : 120} disabled={pending || uncertain || conflict}
        onChange={(event) => setValue(event.target.value)} onKeyDown={(event) => {if (event.key === "Enter") {event.preventDefault(); void execute();}}} />
    </>}
    {error && <p role="alert">{error}</p>}
    {uncertain && <p>The request may have succeeded. Retry confirms the same operation without creating another copy.</p>}
    {conflict && <p>Return to the list to load its latest state. Your unsaved edits in other tabs are unchanged.</p>}
    <div className="grid-dialog-actions"><Button mode="outline" disabled={pending} onClick={close}>{uncertain || conflict ? "Return to list" : "Cancel"}</Button>
      {!conflict && <Button disabled={pending || (action === "import" && !value.trim())} onClick={() => void execute()}>{pending ? "Working…" : uncertain ? "Retry operation" : labels[action]}</Button>}</div>
  </GridDialog>;
}
